/* eslint-disable camelcase */
import _ from 'lodash';
import {
	mapCartItems,
	mapCartCoupons,
	mapShippingOptions,
	getPriceBreakdown,
	validateAddress,
	validateContact,
	CREDIT_CARD,
	PAYPAL
} from '@js/service/checkoutServiceV3';

export default {
	pageReady(state) {
		return !!state.cart && state.initialized;
	},

	modifyingOrSubmittingOrder(state) {
		return state.modifyingCart || state.submittingOrder;
	},

	items(state) {
		return mapCartItems(state.cart.order_offers);
	},

	itemForOffer(state, getters) {
		return (offerId) => _.find(getters.items, { offerId });
	},

	coupons(state) {
		return mapCartCoupons(state.cart.order_offers);
	},

	hasBumpers(state) {
		return state.bumpers.length > 0;
	},

	isProductBlackListed(state) {
		return state.errors.length && state.errors[0].includes('blocked');
	},

	shippingOptions(state) {
		return mapShippingOptions(
			_.get(state, 'cart.available_shipping_categories', [])
		);
	},

	isDigitalProduct(state, getters) {
		return getters.shippingOptions.length === 0;
	},

	isPhysicalProduct(state, getters) {
		return !getters.isDigitalProduct;
	},

	isInternational(state, getters) {
		return (
			getters.isPhysicalProduct &&
			state.shippingAddress.country !== 'USA'
		);
	},

	contactErrors(state) {
		return validateContact({
			email: state.email,
			password: state.password,
		});
	},

	hasContactErrors(state, getters) {
		return getters.contactErrors.length > 0;
	},

	hasUserOrNoContactErrors(state, getters) {
		return !!state.user || !getters.hasContactErrors;
	},

	emailExistsErrorOrChecking(state) {
		return state.emailExistsError || state.checkingEmailExists;
	},

	contactVerified(state, getters) {
		return getters.hasUserOrNoContactErrors && !state.emailExistsErrorOrChecking;
	},

	billingAddressErrors(state) {
		const fields = validateAddress(state.billingAddress);

		if (!state.billingAddress.phone_number) {
			fields.push('phone_number');
		}

		return fields;
	},

	hasBillingAddressErrors(state, getters) {
		return getters.billingAddressErrors.length > 0;
	},

	billingAddressVerified(state, getters) {
		return !getters.hasBillingAddressErrors && !state.billingPostalCodeMismatchError;
	},

	shippingAddressErrors(state) {
		const fields = validateAddress(state.shippingAddress);

		if (!state.shippingAddress.phone_number) {
			fields.push('phone_number');
		}

		return fields;
	},

	hasShippingAddressErrors(state, getters) {
		return getters.shippingAddressErrors.length > 0;
	},

	shippingAddressVerified(state, getters) {
		return !getters.hasShippingAddressErrors && !state.shippingPostalCodeMismatchError;
	},

	billingAddressVisible(state, getters) {
		return getters.isDigitalProduct || state.shippingDifferentFromBilling;
	},

	shippingAddressVisible(state, getters) {
		return getters.isPhysicalProduct || state.shippingDifferentFromBilling;
	},

	mainAddress(state, getters) {
		return getters.isDigitalProduct
			? state.billingAddress
			: state.shippingAddress;
	},

	mainAddressErrors(state, getters) {
		return getters.isDigitalProduct
			? getters.billingAddressErrors
			: getters.shippingAddressErrors;
	},

	addressToShip(state, getters) {
		if (getters.isPhysicalProduct) return state.shippingAddress;

		return state.shippingDifferentFromBilling
			? state.shippingAddress
			: state.billingAddress;
	},

	addressToBill(state, getters) {
		if (getters.isDigitalProduct) return state.billingAddress;

		return state.shippingDifferentFromBilling
			? state.billingAddress
			: state.shippingAddress;
	},

	formHasErrors(state, getters) {
		if (!getters.contactVerified) return true;

		return (getters.shippingAddressVisible && !getters.shippingAddressVerified) || (getters.billingAddressVisible && !getters.billingAddressVerified);
	},

	priceBreakdown(state, getters) {
		return getPriceBreakdown(
			state.cart,
			getters.isDigitalProduct ? !getters.hasBillingAddressErrors : getters.shippingAddressSynced
		);
	},

	shippingAddressSynced(state, getters) {
		if (getters.hasShippingAddressErrors) {
			return false;
		}

		if (!state.cart.shipping_address) return false;

		return (
			state.cart.shipping_address.postal_code ===
			state.shippingAddress.postal_code &&
			state.cart.shipping_address.country ===
			state.shippingAddress.country
		);
	},

	paypalOrderOptions(state) {
		return {
			requestBillingAgreement: true,
			flow: 'checkout',
			amount: state.cart.amount_base.decimal,
			currency: 'USD',
			enableShippingAddress: false,
			shippingAddressEditable: false,
		};
	},

	payingWithCreditCard(state) {
		return state.paymentProcessor === CREDIT_CARD;
	},

	enablePaypal(state) {
		return state.paymentProcessors.includes(PAYPAL);
	},

	payingWithPaypal(state) {
		return state.paymentProcessor === PAYPAL;
	},
};

